import { useCallback, useMemo } from 'react';

import { useLocation } from 'react-router';

import { useNavigate } from '@change-corgi/core/react/router';
import { useTracking } from '@change-corgi/core/react/tracking';
import { useUtilityContext } from '@change-corgi/core/react/utilityContext';

import { useStarterDashboardV2PageData } from 'src/app/pages/starterDashboardV2/pageContext';
import { useFlyerDownloadModalContext } from 'src/app/pages/starterDashboardV2/shared/components';
import {
	COMPLETE_TASK_ERROR_MAP,
	useCompleteTask,
	useTriggerSharePetitionModal,
} from 'src/app/pages/starterDashboardV2/shared/hooks';
import { useCurrentUserId, useSessionUuid } from 'src/app/shared/hooks/session';
import { useShortUrls } from 'src/app/shared/hooks/shortUrls';

import { useIsMobile } from '../../../../../../../../hooks/useIsMobile';
import { useShareUrl } from '../../../../../GrowPetition/components/ShareYourPetition/components/ShareUrl/hooks/useShareUrl';

import { getShareUrlWithUtmParams } from './getShareUrlWithUtmParams';
import { createTaskAction, type GetActionParams } from './taskHandlers';

export function useGetAction() {
	const {
		data: {
			petition,
			featureConfigs: { multipleQrCodeFlyersEnabled },
		},
		actions: { refreshData },
	} = useStarterDashboardV2PageData();
	const showMultiDownloadFlyersTask = multipleQrCodeFlyersEnabled;
	const isMobile = useIsMobile();
	const track = useTracking();

	const { openModal } = useFlyerDownloadModalContext();
	const triggerSharePetitionModal = useTriggerSharePetitionModal();

	const navigate = useNavigate();
	const { pathname, search } = useLocation();
	const { resolveUrl } = useUtilityContext();
	const currentUserId = useCurrentUserId();
	const sessionUuid = useSessionUuid();
	const { mutate: completeTask } = useCompleteTask({
		onError: (error) => {
			if (error === COMPLETE_TASK_ERROR_MAP.UNAUTHORIZED_ERROR.code) {
				const params = encodeURIComponent(`${pathname}${search}`);
				navigate(`/login_or_join?redirectTo=${params}`);
			}
		},
		completionType: 'manual',
		version: 'new_12_2025',
	});

	const { data, actions } = useShareUrl();

	const longShareUrl = useMemo(
		() =>
			getShareUrlWithUtmParams({
				starterVanityUrl: petition.starterVanityUrl,
				slug: petition.slug,
				resolveUrl,
				currentUserId,
				sessionUuid,
			}),
		[petition.starterVanityUrl, petition.slug, resolveUrl, currentUserId, sessionUuid],
	);

	const shortUrls = useShortUrls({ share: longShareUrl });
	const shareUrlWithUtmParams = shortUrls.share;

	return useCallback(
		(params: GetActionParams) =>
			createTaskAction(
				{ ...params, version: params.version ?? 'new_12_2025' },
				{
					petition,
					navigate,
					track,
					isMobile,
					shareUrlWithUtmParams,
					refreshData,
					completeTask,
					triggerSharePetitionModal,
					openFlyerDownloadModal: openModal,
					showMultiDownloadFlyersTask,
					data,
					actions,
				},
			),
		[
			petition,
			shareUrlWithUtmParams,
			refreshData,
			showMultiDownloadFlyersTask,
			openModal,
			completeTask,
			navigate,
			triggerSharePetitionModal,
			isMobile,
			track,
			data,
			actions,
		],
	);
}
